import type { ObjectId } from '@freightview/object-id'

import {
  vFallback,
  vObject,
  vObjectId,
  vOptional,
  type vOutput,
  vPicklist,
  vString,
  vStringToNumber,
} from '../validation/index.ts'
import type { AuthAttemptState } from './auth-attempt-state.ts'
import type { ApplicationType } from './common.ts'

export const userEntityTypes = ['admin', 'carrier', 'vendor'] as const
export type UserEntityType = (typeof userEntityTypes)[number]
export const userTypes = [...userEntityTypes, 'shipper'] as const
export type UserType = (typeof userTypes)[number]

export interface UserProfile {
  firstName: string | null
  lastName: string | null
  role: string | null
  phone?: string | null
}

export type UserSession = {
  active?: boolean
  lastUsed: Date
  created: Date
  userAgent?: string
  ip?: string
  application: ApplicationType
  _id: ObjectId
}

export interface UserVendorMetaData {
  recentAccountId?: ObjectId
}

export type User = {
  _id: ObjectId
  created: Date
  carrierId?: ObjectId
  email: string
  isVerified: boolean
  type?: UserEntityType
  profile?: UserProfile
  accounts?: string[]
  vendorMetaData?: UserVendorMetaData
  authAttempts?: AuthAttemptState
  lastLogin?: Date
  lastActivity?: Date
  disabled?: boolean
  chatConfig?: UserChatConfig
}

export interface UserChatConfig {
  tokenLimit: number
}

export type AdminCarrierUser = Omit<User, 'accounts'> & {
  accounts: Array<{ _id: ObjectId; name: string }>

  carrier: {
    _id: ObjectId
    name: string
    code?: string
  }
}

export const changeUserCarrierSchema = vObject({
  carrierId: vObjectId(),
})

export type ChangeUserCarrierDTO = vOutput<typeof changeUserCarrierSchema>

export const adminUserSearchSchema = vObject({
  search: vString(),
  skip: vFallback(vStringToNumber(), 0),
  limit: vFallback(vStringToNumber(), 20),
  userType: vOptional(vPicklist(userTypes.filter(u => u !== 'admin'))),
})

export type AdminUserSearchDTO = vOutput<typeof adminUserSearchSchema>
